import { useEffect } from 'react';

import { Link, useParams } from 'react-router-dom';

import { KeyDocumentsSection } from './components/documents-overview';
import { KeyEntitiesSection } from './components/entities-overview';
import { IssuesOverview } from './components/issues-overview';
import { Summary } from './components/summary';
import { SummaryLoader } from './components/summary-loader';
import { AskKim } from '../../components/ask-kim';
import { SubsetStatCard } from '../../components/subset-stat-card';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useGetSubsetOverview } from '@/api/queries/useGetSubsetOverview';
import { useGetSubsetPersons } from '@/api/queries/useGetSubsetPersons';
import { useGetSummary } from '@/api/queries/useGetSummary';
import formatDate from '@/helpers/formatDate';
import { hasAnyOngoingRun } from '@/helpers/runs';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/skeleton';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { cn } from '@/new/lib/utils';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';

export function SubsetOverview() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const processingState = useMatterProcessingStore((state) => state.getProcessingState(matterId));
  const subsetRuns = processingState?.allProcessingRuns?.filter((run) => run.caseId === subsetId) ?? [];
  const isAnyRunOngoing = hasAnyOngoingRun(subsetRuns);

  const { data: subset } = useGetSubset(matterId, subsetId);
  const isSubsetProcessing = subset?.status === 'processing';
  const isSubsetGenerating = isSubsetProcessing || isAnyRunOngoing;

  const {
    data: summaries,
    isLoading: summaryIsLoading,
    refetch: refetchSummaries,
  } = useGetSummary({ matterId, subsetId });
  const {
    data: overview,
    isLoading: overviewIsLoading,
    refetch: refetchOverview,
  } = useGetSubsetOverview(matterId, subsetId);
  const { data: matter } = useGetMatterV2(matterId);
  const { data: personsData } = useGetSubsetPersons(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: null, // This is the subset overview page
  });

  useEffect(() => {
    if (!isSubsetGenerating) {
      refetchOverview();
      refetchSummaries();
    }
  }, [isSubsetGenerating, refetchOverview, refetchSummaries]);

  useEffect(() => {
    trackPageView(PageName.SubsetSummary);
  }, []);

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <PageContainer>
        {isSubsetProcessing && (
          <div className="mt-4 mb-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <Icon className="lucide--loader-2 h-5 w-5 animate-spin text-amber-600" />
            <p className="font-medium text-amber-800">Creating subset...</p>
          </div>
        )}

        <PageHeader>
          <div className="flex items-center gap-6">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 px-0.5 text-sm text-gray-500">
                <Icon className="lucide--calendar" />
                <div>Created {formatDate(subset?.createdDate ?? '')}</div>
              </div>

              {subset ? (
                <PageTitle text={subset?.name ?? ''} iconClass="lucide--group" />
              ) : (
                <Skeleton className="mt-2 h-8 w-38" />
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button asChild variant="primary" disabled={isSubsetGenerating}>
              <Link
                to={`/matters/${matterId}/subsets/${subsetId}/edit`}
                onClick={() => {
                  trackCustomEvent(TrackingEventsV2.EditExistingSubsetFromSummary, { subsetId });
                }}
                className={cn(isSubsetGenerating && 'pointer-events-none opacity-50')}
              >
                <Icon className="lucide--group h-4 w-4" />
                Edit Subset Issues or Documents
              </Link>
            </Button>
          </div>
        </PageHeader>

        <div className="mb-6 flex flex-wrap items-stretch gap-3">
          <SubsetStatCard
            value={isSubsetProcessing ? '-' : overview?.totalFacts?.toLocaleString()}
            to={isSubsetProcessing ? undefined : `/matters/${matterId}/subsets/${subsetId}/facts`}
            onClick={
              isSubsetProcessing
                ? undefined
                : () =>
                    trackCustomEvent(TrackingEventsV2.ViewFactsFromSubsetSummary, {
                      matterId,
                      subsetId,
                      isAllDocuments: subset?.isAllDocuments,
                    })
            }
          >
            <Icon className="lucide--search text-gray-500" />
            <p>Facts</p>
          </SubsetStatCard>

          <SubsetStatCard
            value={isSubsetProcessing ? '-' : overview?.disputedFacts?.toLocaleString()}
            to={isSubsetProcessing ? undefined : `/matters/${matterId}/subsets/${subsetId}/inconsistencies`}
          >
            <Icon className="lucide--alert-triangle text-gray-500" />
            <p>Inconsistencies</p>
          </SubsetStatCard>

          <SubsetStatCard
            value={isSubsetProcessing ? '-' : overview?.documents?.toLocaleString()}
            to={isSubsetProcessing ? undefined : `/matters/${matterId}/subsets/${subsetId}/docs`}
            onClick={
              isSubsetProcessing
                ? undefined
                : () =>
                    trackCustomEvent(TrackingEventsV2.ViewDocumentsFromSubsetSummary, {
                      matterId,
                      subsetId,
                      isAllDocuments: subset?.isAllDocuments,
                    })
            }
          >
            <Icon className="lucide--file-text text-gray-500" />
            <p>Documents</p>
          </SubsetStatCard>

          <SubsetStatCard
            value={isSubsetProcessing ? '-' : personsData?.length?.toLocaleString()}
            to={isSubsetProcessing ? undefined : `/matters/${matterId}/subsets/${subsetId}/entities`}
            onClick={
              isSubsetProcessing
                ? undefined
                : () =>
                    trackCustomEvent(TrackingEventsV2.ViewEntitiesFromSubsetSummary, {
                      matterId,
                      subsetId,
                      isAllDocuments: subset?.isAllDocuments,
                    })
            }
          >
            <Icon className="lucide--users-round text-gray-500" />
            <p>Entities</p>
          </SubsetStatCard>
        </div>

        {summaryIsLoading ? (
          <SummaryLoader />
        ) : (
          <div className="space-y-6">
            {(() => {
              const hasSummary = Boolean(summaries?.content?.[0]?.content?.trim());
              const themes = summaries?.content?.slice(1) ?? [];
              const hasIssues = themes.length > 0 || summaryIsLoading;
              return (
                <>
                  {subset && <AskKim defaultSubsetId={subsetId} subsets={[subset]} hideSubsetSelector={true} />}
                  <div>
                    <Summary
                      summaryType="Subset"
                      summaryText={summaries?.content?.[0]?.content ?? ''}
                      isProcessing={isAnyRunOngoing}
                      isLoading={summaryIsLoading}
                      hasIssues={hasIssues}
                    />

                    <IssuesOverview issues={themes} isLoading={summaryIsLoading} hasSummary={hasSummary} />
                  </div>

                  <div className="mb-6 flex gap-4">
                    <KeyDocumentsSection overview={overview} overviewIsLoading={overviewIsLoading} />
                    <KeyEntitiesSection />
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </PageContainer>
    </>
  );
}
